import { useState } from "react";

interface FooterSectionProps {
  onNavigate: (id: string) => void;
}

export default function FooterSection({ onNavigate }: FooterSectionProps) {
  const [email, setEmail] = useState("");
  const [subStatus, setSubStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubStatus("submitting");
    try {
      const body = new URLSearchParams({ email });
      const res = await fetch("https://readdy.ai/api/form/d6utjilt4s7j0tv5i9e0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) { setSubStatus("success"); setEmail(""); }
      else setSubStatus("error");
    } catch { setSubStatus("error"); }
  };

  return (
    <footer id="footer" className="bg-[#3D2817] pt-16 sm:pt-20 md:pt-24 pb-8 sm:pb-10">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 md:px-10">
        {/* Top Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-12 pb-12 sm:pb-16 border-b border-[#FAF8F3]/10">
          {/* Col 1 – Brand */}
          <div>
            <img
              src="https://static.readdy.ai/image/61892072331720d5ae52870a50f3ccd8/5abd40eda45740105ef90e7db4972552.png"
              alt="Rustic Copper"
              className="h-16 w-auto mb-6"
            />
            <p
              className="text-[#D4AF87]/70 text-sm italic mb-5"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Handcrafted with intention.
            </p>
            <p
              className="text-[#FAF8F3]/50 text-xs leading-relaxed max-w-[260px]"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 300 }}
            >
              Each piece of Rustic Copper begins as raw material and becomes
              something permanent — a statement worn close to the skin.
            </p>
          </div>

          {/* Col 2 – Newsletter */}
          <div>
            <p
              className="text-[#D4AF87] text-xs uppercase tracking-[3px] mb-7"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Stay Connected
            </p>
            {subStatus === "success" ? (
              <p className="text-[#FAF8F3]/70 text-sm italic" style={{ fontFamily: "Playfair Display, serif" }}>
                Welcome to the Rustic Copper circle.
              </p>
            ) : (
              <form id="newsletter-form" data-readdy-form onSubmit={handleSubscribe}>
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  className="w-full bg-transparent border-b border-[#FAF8F3]/25 py-3 text-sm text-[#FAF8F3]/80 placeholder:text-[#FAF8F3]/35 focus:outline-none focus:border-[#D4AF87] transition-colors mb-3"
                  style={{ fontFamily: "Inter, sans-serif" }}
                />
                <button
                  type="submit"
                  disabled={subStatus === "submitting"}
                  className="flex items-center gap-2 text-[#B76E79] text-xs uppercase tracking-[2px] hover:text-[#D4AF87] transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  {subStatus === "submitting" ? "Subscribing..." : <>Subscribe <span>→</span></>}
                </button>
                <p className="text-[#FAF8F3]/30 text-[10px] mt-3" style={{ fontFamily: "Inter, sans-serif" }}>
                  We respect your privacy.
                </p>
              </form>
            )}
          </div>

          {/* Col 3 – Quick Links */}
          <div>
            <p
              className="text-[#D4AF87] text-xs uppercase tracking-[3px] mb-7"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Explore
            </p>
            <ul className="space-y-3">
              {[
                { label: "Collection", id: "collection" },
                { label: "Custom Jewelry", id: "custom" },
                { label: "Our Story", id: "editorial" },
                { label: "Care Guide", id: "footer" },
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => onNavigate(link.id)}
                    className="text-[#FAF8F3]/60 text-sm hover:text-[#D4AF87] transition-colors duration-300 cursor-pointer whitespace-nowrap group flex items-center gap-2"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 300 }}
                  >
                    <span className="w-0 group-hover:w-3 h-px bg-[#D4AF87] transition-all duration-300 inline-block" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 – Connect */}
          <div>
            <p
              className="text-[#D4AF87] text-xs uppercase tracking-[3px] mb-7"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              Connect
            </p>
            <ul className="space-y-3 mb-8">
              {[
                { icon: "ri-instagram-line", label: "Instagram", href: "#" },
                { icon: "ri-pinterest-line", label: "Pinterest", href: "#" },
                { icon: "ri-facebook-circle-line", label: "Facebook", href: "#" },
              ].map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    rel="nofollow"
                    className="flex items-center gap-3 text-[#FAF8F3]/60 text-sm hover:text-[#D4AF87] transition-colors duration-300 group"
                    style={{ fontFamily: "Inter, sans-serif", fontWeight: 300 }}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${s.icon} text-base`} />
                    </span>
                    {s.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="border-t border-[#FAF8F3]/10 pt-6">
              <p className="text-[#FAF8F3]/40 text-xs mb-1" style={{ fontFamily: "Inter, sans-serif" }}>
                
              </p>
              <p className="text-[#FAF8F3]/40 text-xs" style={{ fontFamily: "Inter, sans-serif" }}>
                +1 (774) 578-3244
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 sm:pt-8">
          <p
            className="text-[#FAF8F3]/35 text-xs text-center md:text-left"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            &copy; 2026 Rustic Copper. All rights reserved.
          </p>
          <div className="flex items-center gap-4 sm:gap-6">
            {["Privacy Policy", "Terms of Service"].map((item) => (
              <a
                key={item}
                href="#"
                rel="nofollow"
                className="text-[#FAF8F3]/35 text-xs hover:text-[#D4AF87] transition-colors duration-300"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                {item}
              </a>
            ))}
          </div>
        </div>

        {/* Agency Credit */}
        <div className="flex flex-col items-center justify-center gap-2 pt-6 sm:pt-8 border-t border-[#FAF8F3]/10 mt-5 sm:mt-6">
          <p
            className="text-[#FAF8F3]/30 text-[10px] uppercase tracking-[2px]"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            desenvolvido por
          </p>
          <a
            href="https://www.twogatheragency.com"
            target="_blank"
            rel="nofollow noreferrer"
            className="opacity-60 hover:opacity-100 transition-opacity duration-300 cursor-pointer"
          >
            <img
              src="https://storage.readdy-site.link/project_files/a7f81a06-d08f-48ea-a12e-e939acbd7d9b/efd293c2-6de9-493a-b54b-23094ac979f2_Conteudo-Twogather-899-x-599-px-1.png?v=9c57f039f7acee7a242c26c5b38bbb13"
              alt="Twogather Agency"
              className="h-10 w-auto object-contain"
            />
          </a>
        </div>
      </div>
    </footer>
  );
}
